/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Row,
  Spin,
  Col,
  Modal,
  DatePicker,
  message,
} from 'antd';
import Table from 'components/Table';
import { CheckCircleOutlined, CloseCircleOutlined, RedoOutlined } from '@ant-design/icons';
import { getEstadosGlobales } from 'api/catalogos';
import { checkToken } from 'api/utils';
import FormSubmitControls from 'components/FormSubmitControls';
import Select from 'components/Select';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import { onError, onSuccess } from 'utils/handlers';
import { formatReceivedValues, formatReceived } from 'utils/formatValues';
import { DATE_FRONTEND_FORMAT } from 'utils/normalizers';
import {
  getLiquidacionesConfiguracionesDescuentos,
  getLiquidacionConfiguracionDescuento,
  postLiquidacionConfiguracionDescuento,
  patchLiquidacionConfiguracionDescuento,
  deleteLiquidacionConfiguracionDescuento,
} from 'api/nominas/liquidaciones/liquidacionesConfiguracionesDescuentos';
import { getConfiguracionDescuentos } from 'api/nominas/descuentos/configuracionDescuentos';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
  ESTATUS_CANCELADO,
  ESTATUS_INACTIVO,
  ESTATUS_RECHAZADO,
} from 'utils/constants';

export const liquidacionConfiguracionDescuentoPermission = {
  permissionModel: 'liquidacionconfiguraciondescuento',
  catalogsModels: [],
  tabsModels: [],
};

const LiquidacionesConfiguracionesDescuentos = ({ permission, liquidacionConfiguracion }) => {
  const [loading, setLoading] = useState(false);
  const [open, setVisible] = useState(false);
  const [estados, setEstados] = useState([]);
  const [data, setData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [form] = Form.useForm();
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [configuracionDescuentos, setConfiguracionDescuentos] = useState([]);
  const [descuentoDelete, setDescuentoDelete] = useState();
  const [descuentosFiltered, setDescuentosFiltered] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [disableReactivar, setDisableReactivar] = useState(true);
  const [disableCancelar, setDisableCancelar] = useState(false);
  // Un registro Autorizado o Cancelado ya no debe poder editarse ni eliminarse.
  const [disableEditDelete, setDisableEditDelete] = useState(true);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [visibleReactivarConfirmation, setVisibleReactivarConfirmation] = useState(false);
  const [visibleCancelarConfirmation, setVisibleCancelarConfirmation] = useState(false);

  const formInstances = [
    form,
  ];

  const formatReceivedClone = (values) => {
    if (Array.isArray(values)) {
      let valueClone = values.map((e) => formatReceivedValues(e));
      valueClone = valueClone.map((e) => ({
        ...e,
      }));
      return valueClone;
    }
    return formatReceivedValues(values);
  };

  const fetchData = async () => {
    setLoading(true);
    const _data = await getLiquidacionesConfiguracionesDescuentos();
    setData(formatReceivedClone(_data));
    setLoading(false);
  };

  const filterDescuentos = (_configuracionDescuentos) => {
    const _descuentosFiltered = _configuracionDescuentos.filter((desc) => !data.some((val) => val?.descuento === desc?.id));
    return _descuentosFiltered;
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _configuracionDescuentos,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getConfiguracionDescuentos({ aplica_descuento_para_liquidacion: true, estados_globales: ESTATUS_AUTORIZADO }),
        getLiquidacionesConfiguracionesDescuentos(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setConfiguracionDescuentos(formatReceived(_configuracionDescuentos));
        setData(formatReceivedClone(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setDescuentosFiltered(filterDescuentos(configuracionDescuentos));
    setSelectedRowKeys([]);
    setVisible(false);
    setDisableAuth(false);
    setDisableReactivar(true);
    setDisableCancelar(false);
    setDisableEditDelete(true);
    form.resetFields();
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteLiquidacionConfiguracionDescuento(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    if (!match) {
      setLoading(false);
      return;
    }
    // El doble clic en una fila llama a esta funcion directo, sin pasar por el
    // boton "Editar" del toolbar (ver onRow.onDoubleClick en components/Table.jsx).
    if (match.estados_globales === ESTATUS_AUTORIZADO || match.estados_globales === ESTATUS_CANCELADO) {
      setLoading(false);
      message.warn('No se puede editar un Descuento Autorizado o Cancelado.');
      return;
    }
    const liquidacionConfigDescuento = await getLiquidacionConfiguracionDescuento(match.id);
    const configDescuento = configuracionDescuentos.find((e) => e.id === liquidacionConfigDescuento?.descuento);
    const _descuentosFiltered = filterDescuentos(configuracionDescuentos);
    setTimeout(() => {
      if (liquidacionConfigDescuento) {
        form.resetFields();
        setTimeout(() => {
          form.setFieldsValue({
            ...formatReceivedClone(liquidacionConfigDescuento),
            descuento: configDescuento?.id || null,
          });
          setDescuentosFiltered(_descuentosFiltered.concat(configDescuento));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchLiquidacionConfiguracionDescuento(key, values)
        : postLiquidacionConfiguracionDescuento(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };
  const onClickDelete = () => {
    setDescuentoDelete(configuracionDescuentos.find((desc) => desc.id === form.getFieldValue('descuento'))?.concepto);
    setVisibleAlert(true);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede autorizar un descuento ya autorizado');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchLiquidacionConfiguracionDescuento(key, vals);
      if (res) {
        onSuccess(res);
        await fetchData();
        setConfirmationVisible(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishReactivarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Reactivar un Descuento Activo');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_ACTIVO,
        vigencia_final: null,
      };
      const res = await patchLiquidacionConfiguracionDescuento(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleReactivarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnFinishCancelarConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede cancelar un descuento ya cancelado');
        return;
      }
      const vals = {
        ...form.getFieldsValue(),
        liquidacion_configuracion: liquidacionConfiguracion?.id,
        estados_globales: ESTATUS_CANCELADO,
      };
      const res = await patchLiquidacionConfiguracionDescuento(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleCancelarConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    const isActivo = record?.estados_globales === ESTATUS_ACTIVO;
    const isCancelado = record?.estados_globales === ESTATUS_CANCELADO;
    const isRechazado = record?.estados_globales === ESTATUS_RECHAZADO;
    setDisableAuth(isAutorizado || isInactivo || isCancelado || isRechazado);
    setDisableReactivar(isActivo);
    setDisableCancelar(isCancelado);
    setDisableEditDelete(isAutorizado || isCancelado);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
  };

  const columns = [
    {
      titleText: 'Descuento',
      dataIndex: 'descuento',
      key: 'descuento',
      width: 500,
      render: (val) => {
        const _configuracionesDescuentos = configuracionDescuentos.find((desc) => desc.id === val);
        return _configuracionesDescuentos ? `${_configuracionesDescuentos?.clave} - ${_configuracionesDescuentos?.concepto}` : '';
      },
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 200,
      render: (val) => (val ? val?.format?.(DATE_FRONTEND_FORMAT) ?? val : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 300,
      render: (val) => estados.find((estado) => estado.id === val)?.descripcion,
    },
  ];

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          data={data}
          cols={columns}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          customActions={[
            {
              onClick: () => setConfirmationVisible(true),
              permission: { model: 'liquidacionconfiguraciondescuento', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableAuth,
            },
            {
              onClick: () => setVisibleReactivarConfirmation(true),
              permission: { model: 'liquidacionconfiguraciondescuento', action: 'reactivar' },
              text: 'Reactivar',
              icon: <RedoOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length || disableReactivar,
            },
            {
              onClick: () => setVisibleCancelarConfirmation(true),
              permission: { model: 'liquidacionconfiguraciondescuento', action: 'cancelar' },
              text: 'Cancelar',
              icon: <CloseCircleOutlined />,
              disabledBySelection: false,
              className: 'cancel',
              disabled: !selectedRowKeys?.length || disableCancelar,
            },
          ]}
          permission={permission}
          allowExportPDF={false}
          disableEdit={!selectedRowKeys?.length || disableEditDelete}
          disableDelete={!selectedRowKeys?.length || disableEditDelete}
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Descuento`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
          >
            <Col span={24}>
              <Form.Item
                name="descuento"
                label="Descuento"
                rules={rules.required}
                hasFeedback
              >
                <Select
                  dataSource={descuentosFiltered}
                  render={(val) => `${val?.clave} - ${val?.concepto}`}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="vigencia_final"
                label="Vigencia Final"
                hasFeedback
              >
                <DatePicker
                  format={DATE_FRONTEND_FORMAT}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={1}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                />
              </Form.Item>
            </Col>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Descuento ${descuentoDelete}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => setConfirmationVisible(false)}
          open={confirmationVisible}
          contentTitle={`Autorizar el Descuento ${configuracionDescuentos.find((desc) => desc.id === form.getFieldValue('descuento'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishReactivarConfirmation}
          onFinishText="Reactivar"
          onFinishIcon={<RedoOutlined />}
          onCancel={() => {
            setVisibleReactivarConfirmation(false);
            onCancel();
          }}
          open={visibleReactivarConfirmation}
          contentTitle={`Reactivar el Descuento ${configuracionDescuentos.find((desc) => desc.id === form.getFieldValue('descuento'))?.concepto || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishCancelarConfirmation}
          ConfirmCancel
          onCancel={() => {
            setVisibleCancelarConfirmation(false);
            onCancel();
          }}
          open={visibleCancelarConfirmation}
          contentTitle={`Cancelar el Descuento ${configuracionDescuentos.find((desc) => desc.id === form.getFieldValue('descuento'))?.concepto || ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default LiquidacionesConfiguracionesDescuentos;
